import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Button, Typography } from '@material-ui/core'
import { RootState } from 'src/store/ducks/rootReducer'
import usePersistedState from 'src/hooks/usePersistedState'
import { postsRequest } from 'src/store/ducks/news/actions'
import { categoriesRequest } from 'src/store/ducks/categories/actions'
import sortByDate from 'src/utils/sortByDate'
import { PUBLISHED_STATUS } from 'src/components/NewsModule/constants'
import NewsCarousel from 'src/components/NewsCarousel/NewsCarousel'
import BreadcrumbsComponent from 'src/components/BreadcrumbsComponent/BreadcrumbsComponent'
import { useGlobalStyles } from 'src/mui/styles/globalStyles'
import SearchInput from 'src/components/SearchInput/SearchInput'
import NewsMostRecentCarousel from 'src/components/NewsMostRecentCarousel/NewsMostRecentCarousel'
import { AudienceNewsEnum, CategoriesNewsPortugueseEnum } from 'src/types/news'
import { useStyles } from './NewsHomeContent.styles'

const NewsHomeContent = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const { news } = useSelector((state: RootState) => state.news)
  const { categories } = useSelector((state: RootState) => state.categories)

  const [postList, setPostList] = useState<any>([])

  // eslint-disable-next-line
  const [user] = usePersistedState('auth-token', '')
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isLoadingCategories, setIsLoadingCategories] = useState<boolean>(true)

  const [blog, setBlog] = useState<any>()
  const [blogList, setBlogList] = useState<any>([])

  const [event, setEvent] = useState<any>()
  const [eventList, setEventList] = useState<any>([])

  const [publication, setPublication] = useState<any>()
  const [publicationList, setPublicationList] = useState<any>([])

  const [defense, setDefense] = useState<any>()
  const [defenseList, setDefenseList] = useState<any>([])

  const [award, setAward] = useState<any>()
  const [awardList, setAwardList] = useState<any>([])

  const classesGlobal = useGlobalStyles()
  const classes = useStyles()

  useEffect(() => {
    dispatch(postsRequest())
    dispatch(categoriesRequest())
  }, [])

  useEffect(() => {
    if (news && news.length) {
      setPostList(
        sortByDate(news, PUBLISHED_STATUS).filter((post) =>
          user
            ? post.status === PUBLISHED_STATUS
            : post.status === PUBLISHED_STATUS &&
              post.audience === AudienceNewsEnum.External
        )
      )
    }
  }, [news])

  useEffect(() => {
    if (categories.length < 1 && !isLoadingCategories) {
      return
    }

    setBlog(
      categories?.find((e) => e.title === CategoriesNewsPortugueseEnum.Blog)
    )
    setEvent(
      categories?.find((e) => e.title === CategoriesNewsPortugueseEnum.Event)
    )
    setPublication(
      categories?.find(
        (e) => e.title === CategoriesNewsPortugueseEnum.Publication
      )
    )
    setDefense(
      categories?.find((e) => e.title === CategoriesNewsPortugueseEnum.Defense)
    )
    setAward(
      categories?.find((e) => e.title === CategoriesNewsPortugueseEnum.Award)
    )

    setIsLoadingCategories(false)
  }, [categories])

  useEffect(() => {
    if (postList.length > 0 && !isLoadingCategories) {
      setBlogList(
        sortByDate(postList, PUBLISHED_STATUS)
          .filter((post) => post.newsCategoriesId === blog.id)
          .slice(0, 5)
      )
      setEventList(
        sortByDate(postList, PUBLISHED_STATUS)
          .filter((post) => post.newsCategoriesId === event.id)
          .slice(0, 5)
      )
      setPublicationList(
        sortByDate(postList, PUBLISHED_STATUS)
          .filter((post) => post.newsCategoriesId === publication.id)
          .slice(0, 5)
      )
      setDefenseList(
        sortByDate(postList, PUBLISHED_STATUS)
          .filter((post) => post.newsCategoriesId === defense.id)
          .slice(0, 5)
      )
      setAwardList(
        sortByDate(postList, PUBLISHED_STATUS)
          .filter((post) => post.newsCategoriesId === award.id)
          .slice(0, 5)
      )
      setIsLoading(false)
    }
  }, [postList, isLoadingCategories])

  return (
    <>
      <Box className={classesGlobal.containerPages}>
        <Box className={classesGlobal.topPageBreadcrumbsSearch}>
          <BreadcrumbsComponent
            crumbs={[
              { label: t('navbar.start'), path: '/' },
              { label: t('news.title') }
            ]}
          />
          <SearchInput
            redirectUrl="/news/search"
            placeholder={t('news.placeholder-search')}
          />
        </Box>
        <Box>
          <Typography className={classesGlobal.titleSection}>
            {t('news.most-recent')}
          </Typography>
          <NewsMostRecentCarousel categories={categories} items={postList} />
        </Box>
        <Box className={classes.sectionNews}>
          <Typography className={classesGlobal.titleSection}>
            {t('news.categories.publications')}
          </Typography>
          <NewsCarousel
            categories={categories}
            items={publicationList}
            isLoading={isLoading}
          />
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/news/publications"
            className={classes.moreNews}
          >
            <Typography variant="body1" className={classes.moreNewsText}>
              {t('news.see-publications')}
            </Typography>
          </Button>
        </Box>
        <Box className={classes.sectionNews}>
          <Typography className={classesGlobal.titleSection}>
            {t('news.categories.defenses')}
          </Typography>
          <NewsCarousel
            categories={categories}
            items={defenseList}
            isLoading={isLoading}
          />
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/news/defenses"
            className={classes.moreNews}
          >
            <Typography variant="body1" className={classes.moreNewsText}>
              {t('news.see-defenses')}
            </Typography>
          </Button>
        </Box>
        <Box className={classes.sectionNews}>
          <Typography className={classesGlobal.titleSection}>
            {t('news.categories.events')}
          </Typography>
          <NewsCarousel
            categories={categories}
            items={eventList}
            isLoading={isLoading}
          />
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/news/events"
            className={classes.moreNews}
          >
            <Typography variant="body1" className={classes.moreNewsText}>
              {t('news.see-events')}
            </Typography>
          </Button>
        </Box>
        <Box className={classes.sectionNews}>
          <Typography className={classesGlobal.titleSection}>
            {t('news.categories.awards')}
          </Typography>
          <NewsCarousel
            categories={categories}
            items={awardList}
            isLoading={isLoading}
          />
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/news/awards"
            className={classes.moreNews}
          >
            <Typography variant="body1" className={classes.moreNewsText}>
              {t('news.see-awards')}
            </Typography>
          </Button>
        </Box>
        <Box className={classes.sectionNews}>
          <Typography className={classesGlobal.titleSection}>
            {t('news.categories.blog')}
          </Typography>
          <NewsCarousel
            categories={categories}
            items={blogList}
            isLoading={isLoading}
          />
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/news/blog"
            className={classes.moreNews}
          >
            <Typography variant="body1" className={classes.moreNewsText}>
              {t('news.see-blog')}
            </Typography>
          </Button>
        </Box>
      </Box>
    </>
  )
}

export default NewsHomeContent
